import { useStreamingReport } from '../hooks/useStreamingReport';
import { Button, ConfigProvider, message, Input } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { DownloadOutlined, LockOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';

const HEPAN_FOLLOW_UP_LIMIT = 2;
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useExportImage } from '../hooks/useExportImage';
import XunhupayButton from '../components/XunhupayButton';
import weixinQR from '../assets/weixin.jpg';
import './Report.css';
import './HepanReport.css';
import StreamingStatus from '../components/StreamingStatus';

interface HepanReportProps {
  person1Data: any;
  person2Data: any;
  relationshipType: string;
  aiAnswer: string;
  onBack: () => void;
  initialUnlocked?: boolean;
  preValidatedInviteCode?: string;
  restoredReport?: { person1Data: any; person2Data: any; aiAnswer: string; relationshipType: string } | null;
  compressedReport?: string; // 后端返回的压缩报告数据
  reportToken?: string;
  generating?: boolean;
  generationError?: string;
  onFollowUpLoadingChange?: (loading: boolean) => void;
}

// 价格配置（从环境变量读取，默认值为原硬编码值）
const PRICE_CONFIG = {
  original: parseFloat(import.meta.env.VITE_PRICE_ORIGINAL || '59.9'),
  current: parseFloat(import.meta.env.VITE_PRICE_CURRENT || '18.88'),
};

// 章节配置：定义免费和付费章节
const CHAPTER_CONFIG: { [key: string]: { title: string; free: boolean }[] } = {
  'couple': [
    { title: '契合度综合评分', free: true },
    { title: '你们到底是什么关系形成因素', free: true },
    { title: '日常相处有多搭', free: false },
    { title: '吵架急救包', free: false },
    { title: '好好说话指南', free: false },
    { title: '关系活力保鲜剂', free: false },
    { title: '未来三年感情剧透', free: false },
  ],
  'ambiguous': [
    { title: '契合度综合评分', free: true },
    { title: '当前磁场鉴定', free: true },
    { title: '滤镜与现实', free: false },
    { title: '下头瞬间与避雷针', free: false },
    { title: '直球还是拉扯', free: false },
    { title: '定心丸配方', free: false },
    { title: '赏味期限', free: false },
  ],
  'friend': [
    { title: '契合度综合评分', free: true },
    { title: '友谊基底鉴定', free: true },
    { title: '性格拼图与默契度', free: false },
    { title: '友谊翻船预警', free: false },
    { title: '走心夜聊解码器', free: false },
    { title: '情绪价值充值站', free: false },
    { title: '未来三年友谊路标', free: false },
  ],
  'partner': [
    { title: '契合度综合评分', free: true },
    { title: '搞钱基因鉴定', free: true },
    { title: '搞钱分工地图', free: false },
    { title: '散伙红线与防坑预警', free: false },
    { title: '商业沟通解码', free: false },
    { title: '动力泵与定心丸', free: false },
    { title: '未来三年搞钱风口', free: false },
  ],
  'parent_child': [
    { title: '契合度综合评分', free: true },
    { title: '亲子羁绊底层代码', free: true },
    { title: '代沟与滤镜拆解', free: false },
    { title: '防雷防爆指南', free: false },
    { title: '跨服聊天解码器', free: false },
    { title: '安全感储蓄罐', free: false },
    { title: '未来三年亲子趋势', free: false },
  ],
  'sibling': [
    { title: '契合度综合评分', free: true },
    { title: '手足羁绊底层代码', free: true },
    { title: '手足相处模式', free: false },
    { title: '手足翻船预警', free: false },
    { title: '手足沟通解码器', free: false },
    { title: '手足互助指南', free: false },
    { title: '未来三年手足趋势', free: false },
  ],
};

// 解压 Base64 字符串为数据
export const decompressData = (compressed: string): any => {
  try {
    const jsonStr = decodeURIComponent(atob(compressed));
    return JSON.parse(jsonStr);
  } catch (e) {
    console.error('解压数据失败:', e);
    return null;
  }
};

const HepanReport: React.FC<HepanReportProps> = ({
  person1Data: initialPerson1Data,
  person2Data: initialPerson2Data,
  relationshipType: initialRelationshipType,
  aiAnswer: initialAiAnswer,
  onBack,
  initialUnlocked = false,
  preValidatedInviteCode,
  restoredReport,
  compressedReport: initialCompressedReport,
  reportToken,
  generating = false,
  generationError,
  onFollowUpLoadingChange,
}) => {
  // 使用恢复的报告数据或初始数据
  const person1Data = restoredReport?.person1Data || initialPerson1Data;
  const person2Data = restoredReport?.person2Data || initialPerson2Data;
  const relationshipType = restoredReport?.relationshipType || initialRelationshipType;
  const [unlockedAnswer, setUnlockedAnswer] = useState('');
  const aiAnswer = unlockedAnswer || restoredReport?.aiAnswer || initialAiAnswer;
  const relationshipMap: { [key: string]: string } = {
    'couple': '恋人 / 夫妻',
    'ambiguous': '暧昧期 / 友达以上',
    'friend': '朋友 / 知己',
    'partner': '搞钱搭子 / 合伙人',
    'parent_child': '父母 / 子女',
    'sibling': '兄弟姐妹'
  };

  const reportRef = useRef<HTMLDivElement>(null);
  const consumedInvite = useRef('');
  const { exportImage } = useExportImage('双人关系分析报告');
  
  // 支付状态 - 如果传入 initialUnlocked 为 true，则默认已解锁
  const [isUnlocked, setIsUnlocked] = useState(initialUnlocked);
  useEffect(() => { if (initialUnlocked) setIsUnlocked(true); }, [initialUnlocked]);
  
  // 邀请码状态
  const [inviteCode, setInviteCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [unlockedByInvite, setUnlockedByInvite] = useState(Boolean(preValidatedInviteCode));
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [liveFollowUp, setLiveFollowUp] = React.useState('');
  const { run: runFollowUpStream, streaming: followUpStreaming, error: followUpStreamError } = useStreamingReport();
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpHistory, setFollowUpHistory] = useState<Array<{ question: string; answer: string }>>([]);
  const remainingFollowUps = HEPAN_FOLLOW_UP_LIMIT - followUpHistory.length;

  // 只在打开报告时滚动，后续增量更新不打断阅读。
  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 如果有预验证的邀请码且已解锁，自动消耗
    if (preValidatedInviteCode && initialUnlocked && !generating && !generationError && consumedInvite.current !== preValidatedInviteCode) {
      consumedInvite.current = preValidatedInviteCode;
      fetch('/api/invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'consume',
          code: preValidatedInviteCode.trim(),
          type: 'hepan'
        })
      }).catch(console.error);
    }
    
  }, [preValidatedInviteCode, initialUnlocked, generating, generationError]);

  // 解析报告内容为章节
  const parseChapters = (content: string) => {
    const chapters: { title: string; content: string; free: boolean }[] = [];
    const config = CHAPTER_CONFIG[relationshipType] || CHAPTER_CONFIG['couple'];
    
    // 按 "### 第X步" 或 "### 第X章" 分割
    const chapterRegex = /###\s+(第[一二三四五六七八九十]+[步章]|契合度综合评分)[：:]?\s*([^\n]*)/g;
    const matches = [...content.matchAll(chapterRegex)];
    
    if (matches.length === 0) {
      // 如果没有匹配到章节，返回完整内容作为第一章
      return [{ title: '完整报告', content, free: true }];
    }
    
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const startIndex = match.index!;
      const endIndex = i < matches.length - 1 ? matches[i + 1].index! : content.length;
      const chapterContent = content.slice(startIndex, endIndex).trim();
      
      // 查找对应的配置
      const configItem = config.find(c => 
        chapterContent.includes(c.title) || match[2]?.includes(c.title)
      );
      const fallbackItem = config[i];
      
      chapters.push({
        title: configItem?.title || fallbackItem?.title || match[2] || '未知章节',
        content: chapterContent,
        free: configItem?.free ?? fallbackItem?.free ?? false
      });
    }
    
    return chapters;
  };

  const chapters = parseChapters(aiAnswer);
  const freeChapters = reportToken && !isUnlocked ? [{ title: '免费预览', content: aiAnswer, free: true }] : chapters.filter(c => c.free);
  const paidChapters = chapters.filter(c => !c.free);

  const handleExport = () => {
    exportImage('.hepan-report-content');
  };

  // 处理支付成功
  const handlePaySuccess = async (tradeNo: string) => {
    try {
      const response = await fetch(`/api/xunhupay-report?tradeNo=${encodeURIComponent(tradeNo)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '获取完整报告失败');
      const report = decompressData(payload.data);
      if (!report?.aiAnswer) throw new Error('完整报告数据无效');
      setUnlockedAnswer(report.aiAnswer);
      setIsUnlocked(true);
      message.success('支付成功！现在可以查看完整报告');
    } catch (error) { message.error(error instanceof Error ? error.message : '获取完整报告失败'); }
  };

  // 验证邀请码
  const handleVerifyInviteCode = async () => {
    if (!inviteCode.trim()) {
      message.warning('请输入邀请码');
      return;
    }
    
    if (inviteCode.trim().length < 4) {
      message.error('邀请码太短，至少4位');
      return;
    }
    
    setIsVerifying(true);
    try {
      if (reportToken) {
        const response = await fetch('/api/invite-code', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'redeem', type: 'hepan', code: inviteCode.trim(), reportToken }),
        });
        const payload = await response.json();
        if (!response.ok || (!payload.answer && !payload.generating)) throw new Error(payload.error || '解锁失败');
        if (payload.answer) setUnlockedAnswer(payload.answer);
        setIsUnlocked(true);
        setUnlockedByInvite(true);
        message.success(payload.generating ? '已解锁，正文将继续实时显示' : '完整报告已解锁');
        return;
      }
      const resp = await fetch('/api/invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify',
          code: inviteCode.trim(),
          type: 'hepan'
        })
      });
      
      const data = await resp.json();
      
      if (resp.ok && data.valid) {
        // 验证成功，消耗邀请码
        const consumeResp = await fetch('/api/invite-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'consume',
            code: inviteCode.trim(),
            type: 'hepan'
          })
        });
        
        const consumeData = await consumeResp.json();
        
        if (consumeResp.ok) {
          setIsUnlocked(true);
          setUnlockedByInvite(true);
          message.success('邀请码验证成功！已解锁完整报告，并获得 2 次专属追问机会');
        } else {
          message.error(consumeData.error || '邀请码已用完');
        }
      } else {
        message.error(data.error || '邀请码无效');
      }
    } catch (error) {
      console.error('验证邀请码失败:', error);
      message.error('验证失败，请稍后重试');
    } finally {
      setIsVerifying(false);
    }
  };

  const submitFollowUp = async () => {
    if (generating || followUpStreaming) return;
    if (!unlockedByInvite) { message.error('邀请码解锁后可使用追问功能'); return; }
    if (remainingFollowUps <= 0) { message.error('本次报告的 2 次追问机会已用完'); return; }
    const question = followUpQuestion.trim();
    if (!question) { message.error('请输入想继续追问的问题'); return; }

    setLiveFollowUp('');
    setFollowUpLoading(true);
    onFollowUpLoadingChange?.(true);
    try {
      const response = await runFollowUpStream('/api/hepan', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person1: person1Data,
          person2: person2Data,
          relationshipType,
          followUpQuestion: question,
          previousAnswer: aiAnswer,
          followUpHistory,
        }),
      }, (text) => { setLiveFollowUp(text); onFollowUpLoadingChange?.(false); });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || '追问回答生成失败');
      setFollowUpHistory((previous) => [...previous, { question, answer: payload.answer || '暂无追问回答' }]);
      setLiveFollowUp('');
      setFollowUpQuestion('');
      message.success('追问回答已生成');
    } catch (error: any) {
      message.error(error.message || '追问失败，请稍后重试');
    } finally {
      setFollowUpLoading(false);
      onFollowUpLoadingChange?.(false);
    }
  };

  return (
    <ConfigProvider locale={zhCN}> 
      <div className="report-page">
        <div className="report-container">
          {/* 左侧：双人信息概览 */}
          <div className="report-left">
            <div className="report-header">
              <Button type="link" onClick={onBack} disabled={generating} style={{ padding: 0, fontSize: '16px' }}>
                ← 返回
              </Button>
            </div>

            {/* 第一人信息 */}
            <div className="chart-section">
              <h3 className="section-title">{person1Data?.name ?? '第一人'}</h3>
              <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
                <div><strong>出生：</strong>{person1Data?.birthInfo?.solar}</div>
                <div><strong>地点：</strong>{person1Data?.birthInfo?.city}</div>
                <div style={{ marginTop: '12px', color: '#d4af6a', fontWeight: 600 }}>
                  {person1Data?.fourPillars?.year} {person1Data?.fourPillars?.month} {person1Data?.fourPillars?.day} {person1Data?.fourPillars?.hour}
                </div>
              </div>
            </div>

            {/* 第二人信息 */}
            <div className="chart-section">
              <h3 className="section-title">{person2Data?.name ?? '第二人'}</h3>
              <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
                <div><strong>出生：</strong>{person2Data?.birthInfo?.solar}</div>
                <div><strong>地点：</strong>{person2Data?.birthInfo?.city}</div>
                <div style={{ marginTop: '12px', color: '#d4af6a', fontWeight: 600 }}>
                  {person2Data?.fourPillars?.year} {person2Data?.fourPillars?.month} {person2Data?.fourPillars?.day} {person2Data?.fourPillars?.hour}
                </div>
              </div>
            </div>

            {/* 关系类型 */}
            <div className="chart-section">
              <h3 className="section-title">关系类型</h3>
              <div style={{
                fontSize: '18px',
                color: '#d4af6a',
                fontWeight: 600,
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(212, 175, 106, 0.08)',
                borderRadius: '12px'
              }}>
                {relationshipMap[relationshipType] || '未知关系'}
              </div>
            </div>

            {/* 解锁状态 */}
            <div className="chart-section" style={{ 
              background: isUnlocked ? 'rgba(212, 175, 106, 0.15)' : 'rgba(212, 175, 106, 0.08)',
              border: `2px solid ${isUnlocked ? '#d4af6a' : '#d4af6a'}`,
            }}>
              <h3 className="section-title" style={{ color: '#8b6914' }}>{isUnlocked ? '✅ 已解锁完整版' : '🔒 免费预览版'}</h3>
              <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#d4af6a', fontWeight: 500 }}>✦ 免费体验</span>
                  <span style={{ color: '#999' }}>{freeChapters.length} 个章节</span>
                </div>
                {!isUnlocked && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8b6914', fontWeight: 500 }}>✦ 深度解析</span>
                    <span style={{ color: '#999' }}>{paidChapters.length} 个章节</span>
                  </div>
                )}
                {!isUnlocked && (
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#999', textAlign: 'center', padding: '8px', background: 'rgba(212, 175, 106, 0.1)', borderRadius: '6px' }}>
                    解锁后可查看完整深度分析
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 右侧：AI 关系分析报告 */}
          <div className="report-right" ref={reportRef}>
            <div className="report-right-header">
              <h2 className="report-title">✨ 双人关系分析深度解析</h2>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                disabled={generating || Boolean(generationError) || followUpStreaming} onClick={handleExport}
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                  border: 'none',
                  boxShadow: '0 6px 16px rgba(212, 175, 106, 0.4)',
                  fontWeight: 600,
                  fontSize: '16px',
                  height: '48px',
                  padding: '0 32px'
                }}
              >
                🖼️ 保存为图片
              </Button>
            </div>

            {/* 报告内容 */}
            <div className="hepan-report-content">
              {/* 免费章节 */}
              {freeChapters.map((chapter, index) => (
                <div key={`free-${index}`} className="chapter free-chapter" style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(212, 175, 106, 0.3)'
                  }}>
                    ✦ 免费体验
                  </div>
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {chapter.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}

              {/* 付费墙 */}
              <StreamingStatus active={generating} error={generationError} />
              {!isUnlocked && (Boolean(reportToken) || paidChapters.length > 0) && (
                <div className="paywall-container" style={{
                  margin: '40px 0',
                  padding: '40px 32px',
                  background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.12) 0%, rgba(184, 147, 93, 0.08) 100%)',
                  borderRadius: '16px',
                  border: '2px solid rgba(212, 175, 106, 0.4)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                    <LockOutlined style={{ color: '#d4af6a' }} />
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#8b6914',
                    marginBottom: '12px'
                  }}>
                    解锁完整深度解析
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.8',
                    marginBottom: '24px',
                    maxWidth: '480px',
                    margin: '0 auto 24px'
                  }}>
                    以下 {paidChapters.length} 个深度章节已为您生成，包含相处建议、避坑指南和未来趋势预测
                  </p>

                  {/* 所有章节列表 */}
                  <div style={{
                    textAlign: 'left',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    maxWidth: '420px',
                    margin: '0 auto 24px',
                    border: '1px solid rgba(212, 175, 106, 0.2)'
                  }}>
                    {chapters.map((chapter, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 0',
                        borderBottom: index < chapters.length - 1 ? '1px dashed #eee' : 'none',
                        fontSize: '14px',
                        color: chapter.free ? '#d4af6a' : '#666'
                      }}>
                        {chapter.free ? (
                          <span style={{ color: '#52c41a', fontSize: '12px', fontWeight: 600 }}>✓ 已解锁</span>
                        ) : (
                          <LockOutlined style={{ color: '#d4af6a', fontSize: '12px' }} />
                        )}
                        <span style={{ fontWeight: chapter.free ? 500 : 400 }}>{chapter.title}</span>
                      </div>
                    ))}
                  </div>

                  {/* 价格 */}
                  <div style={{ marginBottom: '18px' }}>
                    <span style={{
                      fontSize: '16px',
                      color: '#999',
                      textDecoration: 'line-through',
                      marginRight: '8px'
                    }}>¥{PRICE_CONFIG.original}</span>
                    <span style={{
                      fontSize: '32px',
                      fontWeight: 700,
                      color: '#d4af6a'
                    }}>¥{PRICE_CONFIG.current}</span>
                  </div>

                  {/* 支付按钮（保留展示） */}
                  {!generating && <XunhupayButton
                    reportToken={reportToken}
                    onSuccess={handlePaySuccess}
                    buttonText="支付功能暂不可用"
                    style={{
                      width: '280px',
                      background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 600
                    }}
                    compressedReport={initialCompressedReport}
                  />}

                  {/* 支付状态提示 */}
                  <div style={{
                    marginBottom: '16px',
                    padding: '16px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    borderRadius: '12px',
                    border: '1px solid rgba(212, 175, 106, 0.35)',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      lineHeight: '1.7',
                      marginBottom: '10px'
                    }}>
                      请联系客服微信，付款 18.8元获得邀请码后可直接解锁完整报告
                    </div>
                    <img
                      src={weixinQR}
                      alt="客服微信二维码"
                      style={{
                        width: '170px',
                        height: '170px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                      }}
                    />
                  </div>

                  {/* 分隔线 */}
                  <div style={{
                    margin: '16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    maxWidth: '280px',
                    marginLeft: 'auto',
                    marginRight: 'auto'
                  }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(212, 175, 106, 0.3)' }}></div>
                    <span style={{ fontSize: '13px', color: '#8b6914' }}>或有邀请码</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(212, 175, 106, 0.3)' }}></div>
                  </div>

                  {/* 邀请码输入 */}
                    <Input
                      placeholder="请输入邀请码"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      style={{
                        height: '46px',
                        marginBottom: '12px',
                        fontSize: '15px',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        textAlign: 'center',
                        textTransform: 'uppercase'
                      }}
                      onPressEnter={handleVerifyInviteCode}
                    />
                    <Button
                      type="primary"
                      size="large"
                      loading={isVerifying}
                      onClick={handleVerifyInviteCode}
                      style={{
                        width: '100%',
                        height: '46px',
                        fontSize: '15px',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                        border: 'none'
                      }}
                    >
                      使用邀请码解锁
                    </Button>

                  {/* 免责声明 */}
                  <div style={{
                    marginTop: '16px',
                    fontSize: '12px',
                    color: '#8b6914'
                  }}>
                    本测评基于心理学逻辑建模，结论仅供人际关系参考，不作为人生决策依据
                  </div>
                </div>
              )}

              {/* 付费章节（解锁后显示） */}
              {isUnlocked && paidChapters.map((chapter, index) => (
                <div key={`paid-${index}`} className="chapter paid-chapter" style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #8b6914 0%, #6b4f0b 100%)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(139, 105, 20, 0.3)'
                  }}>
                    ✦ 深度解析
                  </div>
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {chapter.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>

            {unlockedByInvite && (
              <section style={{
                marginTop: '32px', padding: '24px', borderRadius: '16px',
                border: '1px solid rgba(212, 175, 106, 0.35)',
                background: 'linear-gradient(135deg, rgba(250, 247, 240, 0.95), rgba(255, 252, 246, 0.95))',
              }}>
                <h3 style={{ margin: '0 0 8px', color: '#6f5222', fontSize: '18px' }}>邀请码专属追问</h3>
                <p style={{ margin: '0 0 10px', color: '#666', fontSize: '13px', lineHeight: 1.7 }}>你可继续追问这份关系说明书中的具体问题，例如沟通、关系推进、相处边界或合作分工。</p>
                <div style={{ marginBottom: '12px', color: remainingFollowUps > 0 ? '#8c6b2f' : '#b42318', fontSize: '13px', fontWeight: 600 }}>剩余追问次数：{remainingFollowUps} / {HEPAN_FOLLOW_UP_LIMIT}</div>
                <Input.TextArea value={followUpQuestion} onChange={(event) => setFollowUpQuestion(event.target.value)} rows={3} maxLength={160} showCount disabled={remainingFollowUps <= 0 || generating} placeholder="输入你想继续追问的问题" />
                <div style={{ marginTop: '12px', textAlign: 'right' }}>
                  <Button type="primary" loading={followUpLoading} disabled={remainingFollowUps <= 0 || generating} onClick={submitFollowUp} style={{ background: 'linear-gradient(135deg, #d4af6a, #b8935d)', border: 'none', fontWeight: 600 }}>
                    {remainingFollowUps > 0 ? (followUpLoading ? '追问中...' : '提交追问') : '追问次数已用完'}
                  </Button>
                </div>

                {liveFollowUp && <section className="markdown-content" aria-busy={followUpStreaming}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{liveFollowUp}</ReactMarkdown>
                  <StreamingStatus active={followUpStreaming} error={followUpStreamError} />
                </section>}
                {followUpHistory.length > 0 && <div style={{ marginTop: '22px' }}>
                  <h3 style={{ margin: '0 0 12px', color: '#6f5222', fontSize: '17px' }}>追问记录</h3>
                  {followUpHistory.map((item, index) => <article key={index + '-' + item.question} style={{ marginTop: '12px', padding: '18px', borderRadius: '14px', border: '1px solid rgba(212, 175, 106, 0.2)', background: '#fffdf9' }}>
                    <div style={{ marginBottom: '8px', color: '#8c6b2f', fontSize: '14px', fontWeight: 600 }}>追问 {index + 1}</div>
                    <p style={{ margin: '0 0 12px', color: '#333', lineHeight: 1.7 }}><strong>你问：</strong>{item.question}</p>
                    <div className="markdown-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.answer}</ReactMarkdown></div>
                  </article>)}
                </div>}
              </section>
            )}

            {/* 底部区域（仅解锁后显示完整版） */}
            {isUnlocked && (
              <div style={{
                marginTop: '60px',
                padding: '40px 24px',
                background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
                borderRadius: '16px',
                border: '2px solid rgba(212, 175, 106, 0.2)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>✨</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '12px',
                  lineHeight: '1.6'
                }}>
                  感谢您阅读完整报告
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#666',
                  lineHeight: '1.8',
                  marginBottom: '24px',
                  maxWidth: '600px',
                  margin: '0 auto 24px'
                }}>
                  点击上方"保存为图片"按钮，将这份珍贵的双人关系分析报告保存到相册，<br />
                  随时回顾两人关系密码，把握相处之道。
                  <br /><br />
                  <span style={{
                    color: '#d4af6a',
                    fontSize: '13px',
                    background: 'rgba(212, 175, 106, 0.1)',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    ⚠️ 为保护您的个人隐私，本报告不会保存在服务器，请自行保存为图片
                  </span>
                </p>
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined />}
                  disabled={generating || Boolean(generationError) || followUpStreaming} onClick={handleExport}
                  style={{
                    background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                    border: 'none',
                    height: '56px',
                    padding: '0 48px',
                    fontSize: '17px',
                    fontWeight: 600,
                    boxShadow: '0 6px 20px rgba(212, 175, 106, 0.3)',
                  }}
                >
                  立即保存长图
                </Button>
              </div>
            )}

            {/* 底部说明 */}
            <div style={{
              marginTop: '32px',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '16px',
              fontSize: '13px',
              color: '#666',
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              <div>本报告基于行为模型、关系结构模型理论生成</div>
              <div>仅供参考，不构成任何决策建议</div>
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default HepanReport;
